import React, { FC, memo, useEffect, useMemo, useRef } from 'react';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { isRpcStateReady, RpcState, RpcStateReady, RpcStateStatus, useRpcState } from '@om-fe/widgets/dist/common/rpc/rpc-state.hooks'
import { CategoryModel } from '@om-fe/models';
import { TournamentBreadcrumbs } from './tournament-breadcrumbs';
import { breadcrumbsBem, SCROLL_INTO_VIEW_OPTIONS } from './breadcrumbs.constants';
import { GetBreadcrumbsResponse } from '../../common/breadcrumbsResponse';
import DisciplineAnchor from '@om-fe/core/components/discipline-anchor/discipline-anchor';
import TournamentAnchor from '@om-fe/core/components/tournament-anchor/tournament-anchor';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';

interface CategoryBreadcrumbsPublicProps {
    readonly isLarge: boolean;
    readonly disciplineId: string;
    readonly categoryId: string;
    readonly tournamentId?: string;
    readonly routeType: DisciplineRouteType;
    readonly disciplineName: string;
    readonly lang: string;
    readonly eventId?: string;
    readonly isLocationType: boolean;
    readonly isCategoryType: boolean;
    readonly isLive?: boolean;
}

export type CategoryBreadcrumbsProps = CategoryBreadcrumbsPublicProps;

export const CategoryBreadcrumbs: FC<CategoryBreadcrumbsProps> = memo(function CategoryBreadcrumbsComponent(
    {
        lang,
        isLarge,
        disciplineId,
        categoryId,
        tournamentId,
        disciplineName,
        eventId,
        isCategoryType,
        isLocationType,
        routeType,
        isLive,
    }: CategoryBreadcrumbsProps,
) {
    const params = useMemo(() => {
        return { sportId: disciplineId, lang, eventCategoryId: categoryId };
    }, [lang, disciplineId, categoryId]);
    const [response] = useRpcState<GetBreadcrumbsResponse<CategoryModel>>('eventCategories', params);
    const ref: React.RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>();
    const config: OMFE2ConfigModel = getConfig();
    const { status }: RpcState<GetBreadcrumbsResponse<CategoryModel>> = response;

    useEffect(() => {
        if (status === RpcStateStatus.Success && ref.current && !tournamentId) {
            if (!config.virtualState) {
                ref.current.scrollIntoView(SCROLL_INTO_VIEW_OPTIONS);
            }
        }
    }, [status, tournamentId]);

    if (!isRpcStateReady(response)) {
        return null;
    }

    const { data }: RpcStateReady<GetBreadcrumbsResponse<CategoryModel>> = response;

    const categoryName: string | undefined = data.records[0]?.name || categoryId;

    if (tournamentId) {
        return (
            <>
                {isLive ? (
                    <DisciplineAnchor
                        className={breadcrumbsBem('CurrentItem', 'Category')}
                        title={categoryName}
                        disciplineId={disciplineId}
                        disciplineName={disciplineName}
                        childId={categoryId}
                        childName={categoryName}
                        disciplineRouteType={routeType}
                        isLive={isLive}
                    >
                        <span className={breadcrumbsBem('Text', 'Category')}>{categoryName}</span>
                    </DisciplineAnchor>
                ) : (
                    <TournamentAnchor
                        title={'all'}
                        className={breadcrumbsBem('CurrentItem', 'Category')}
                        disciplineId={disciplineId}
                        disciplineName={disciplineName}
                        categoryId={isCategoryType ? categoryId : ''}
                        categoryName={isCategoryType ? categoryName : ''}
                        tournamentId={0}
                        tournamentName={'all'}
                    >
                        <span className={breadcrumbsBem('Text', 'Category')}>{categoryName}</span>
                    </TournamentAnchor>
                )}

                <span className={breadcrumbsBem({
                    element: 'Separator',
                    modifiers: 'Tournament',
                })}
                >&#47;
                </span>

                <TournamentBreadcrumbs
                    tournamentId={tournamentId}
                    isLarge={isLarge}
                    disciplineId={disciplineId}
                    categoryId={categoryId}
                    disciplineName={disciplineName}
                    categoryName={categoryName}
                    lang={lang}
                    eventId={eventId}
                    isCategoryType={isCategoryType}
                    isLocationType={isLocationType}
                />
            </>
        );
    }

    return (
        <span
            className={breadcrumbsBem(isLarge ? 'Text--Category' : 'CurrentItem--Category')}
            ref={ref}
        >
            {categoryName}
        </span>
    );
});
